import { MESSAGE_ARTIFACT_REPORT } from '@/shared/config';
import { checkIcorDevice } from '@/entities/device';
import {
  zeroPad,
  generateFullName,
  dateToString,
  gettextReportType,
  getttextPriority,
} from '@/shared/lib';
import classNames from 'classnames';
import _ from 'lodash';
import { t } from 'ttag';
import { checkNewNotiEventReport } from '@/pages/inbox-details/@x/inbox-list';

/**
 * Builds the badge labels shown on a recent-inbox report row. Always emits a zero-padded study friendly id as a text button that opens the study-information drawer, a report-type label mapping Study to End of Use plus Interim and Notification, and a priority label coloured red for Emergent and orange for Urgent, then appends Biodirect and iCOR badges when the study is Bioflux Direct or runs an iCOR device.
 *
 * @param report - Formatted recent inbox report supplying study, type and priority.
 * @param toggleDisplayDrawer - Callback that opens the study-information drawer for the row.
 * @returns Label descriptors carrying text, class name and an optional click handler.
 */
const generateLabelsRecentReport = (report: any, toggleDisplayDrawer: any) => {
  const labels: {
    text: string;
    className: string;
    isTextButton?: boolean;
    handleClick?: (e: any) => void;
  }[] = [
    {
      text: `${t`Study`} ${zeroPad(report.studyFid)}`,
      className: '--study-fid',
      isTextButton: true,
      handleClick: (e) => {
        e.stopPropagation();
        toggleDisplayDrawer(report, t`Study information`);
      },
    },
    {
      text:
        report.type === 'Study'
          ? t`End of Use`
          : report.type === 'Interim'
            ? t`Interim`
            : report.type === 'Notification'
              ? t`Notification`
              : gettextReportType(report.type),
      className: '--report-type',
    },
    {
      text: getttextPriority(report.priority),
      className: classNames(
        '--priority',
        report.priority === 'Emergent' ? '--red' : report.priority === 'Urgent' ? '--orange' : '',
      ),
    },
  ];
  if (report.isBiofluxDirect) {
    labels.push({
      text: t`Biodirect`,
      className: '--biodirect',
    });
  }
  if (checkIcorDevice(report.study?.device)) {
    labels.push({
      text: t`iCOR`,
      className: '--icor-device',
    });
  }
  return labels;
};

/**
 * Converts a report priority name into a numeric rank so inbox rows can be sorted by urgency. Maps Low, Medium, High, Urgent and Emergent to 1 through 5 and anything unrecognised to 0.
 *
 * @param priority - Report priority name from the inbox payload.
 * @returns Numeric urgency rank, 0 when the priority is unknown.
 */
const getPriorityRank = (priority: string) => {
  switch (priority) {
    case 'Low':
      return 1;
    case 'Medium':
      return 2;
    case 'High':
      return 3;
    case 'Urgent':
      return 4;
    case 'Emergent':
      return 5;
    default:
      return 0;
  }
};

/**
 * Flattens raw recent inbox reports into the row shape the inbox list renders. Lifts study, patient, physician, facility and event fields to the top level, ranks the inbox priority through getPriorityRank, formats eventTime and the dateOfService range with dateToString, and picks technicianComments from the artifact message, new notification findings, joined event comments or the legacy field according to report format and type.
 *
 * @param recentInboxes - Raw recent inbox reports returned by the query.
 * @returns The formatted inbox rows.
 */
const recentInboxesFormatter = (recentInboxes: any) =>
  _.map(recentInboxes, (report) => {
    const { isNewFormat } = report;
    const newEventNotiReport = checkNewNotiEventReport(report);

    return {
      ...report,
      id: report.id,
      reportId: report.reportId,
      type: report.type,
      start: report.start,
      stop: report.stop,
      studyFid: report.study?.friendlyId,
      physicianId: report.study?.info?.interprettingPhysician?.id,
      interprettingPhysicianName: generateFullName(report.study?.info?.interprettingPhysician),
      patientName: generateFullName(report.study?.info?.patient),
      patientId: report.study?.info?.patient?.patientId,
      study: report.study,
      studyType: report.study?.studyType,
      timezone: report.study?.timezone,
      eventFid: report.event?.friendlyId,
      physicianInterpretation: report.physicianInterpretation,
      technicianComments: report.isArtifactReport
        ? MESSAGE_ARTIFACT_REPORT
        : newEventNotiReport
          ? report.reportFindings
          : report.type === 'Event'
            ? _.join(report.event?.comments, ' ')
            : isNewFormat
              ? (report.findings ?? report.technicianComments)
              : report.technicianComments,
      date: report.inbox?.date,
      priority: report.inbox?.priority,
      priorityRank: getPriorityRank(report.inbox?.priority),
      isReviewed: report.inbox?.isReviewed,
      isRead: report.inbox?.isRead,
      eventTimeUTC: report.start,
      eventTime: dateToString({
        startStudyDate: report.study.start,
        isoDate: report.start,
        timezone: report.study?.timezone,
        studyTimeOffset: report.study?.studyTimeOffset,
        facilityCountry: report.study?.facility?.contact?.country,
        facilityTimezone: report.study?.facility?.timezone,
      }),
      dateOfService: `${dateToString({
        isoDate: report.start,
        timezone: report.study?.timezone,
        studyTimeOffset: report.study?.studyTimeOffset,
        format: 'MM/DD/YY',
      })} - ${dateToString({
        isoDate: report.stop,
        timezone: report.study?.timezone,
        studyTimeOffset: report.study?.studyTimeOffset,
        format: 'MM/DD/YY',
      })}`,
      eventId: report.event?.id,
      hasClinicData: report.event?.hasClinicData,
      isNotificationReport: report.type === 'Notification',
      isBiofluxDirect: report.study?.isBiofluxDirect,
      hasSignature: report.hasSignature,
      isArtifactReport: report.isArtifactReport,
      facility: report.study?.facility,
      facilityName: report.study?.facility?.name,
      isNewFormat,
      formatVersion: report.formatVersion,
      aiFormatVersion: report.aiFormatVersion,
      statusReport: report.statusReport,
      lastStatusReportAt: report.lastStatusReportAt,
      isSavingStatus: false,
      profile: report.profile,
      status: report.status,
      isNonPhi: report.study?.isNonPhi,
      lastModified: report.lastModified,
      isRenderingBackground: report.isRenderingBackground,
    };
  });

export { generateLabelsRecentReport, recentInboxesFormatter };
